/**
 * Login Presenter — 登录接口调用封装
 *
 * MVP 架构中的 Presenter 层，负责：
 * 1. 封装用户登录相关的 API 调用
 * 2. 将回调式 API 包装为 Promise，方便 async/await 使用
 *
 * 接口文档参考：接口文档.md #1、#7、#8、#9
 * 登录路由：POST /fg-ai-center/fxzapi/user/get_auth_token
 * 当前用户修改密码路由：POST /fg-ai-center/fxzapi/user/update_current_pwd
 * 忘记密码发送邮箱验证路由：POST /fg-ai-center/fxzapi/user/pwd/forget/vertify_email
 * 忘记密码重置密码路由：POST /fg-ai-center/fxzapi/user/pwd/forget/modify_pwd
 *
 * 注：2026-10-16 起，平台仅支持"富工作扫码登录自动注册"，
 * 不再提供邮箱自助注册入口（前端入口与后端接口均已移除）。
 */

import { postHttp } from '../../utils/service';
import { FXZ_API_URI_PREFIX } from '../../utils/fxzApiService';

/**
 * 富工作扫码登录 — 使用授权 code 换取平台登录态
 * @param {object} params - { code: string, state?: string }
 * @returns {Promise<{status: number, msg: string, token?: string, is_admin?: boolean, force_update_password?: string, username?: string}>}
 */
export function fullworkAuthLogin(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/fullwork_auth_login', params, resolve);
  });
}

/**
 * 富工作扫码登录 — 使用授权 code 换取平台登录态
 * @param {object} params - { code: string, state?: string }
 * @returns {Promise<{status: number, msg: string, token?: string, is_admin?: boolean, force_update_password?: string}>}
 */
export function iamOAuth(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/auth/iamOAuth', params, resolve);
  });
}

/**
 * 用户登录
 * 接口文档 #1 — POST /fxzapi/user/get_auth_token
 * @param {object} params - { username: string, pwd: string }
 * @returns {Promise<{status: number, msg: string, token: string, is_admin: boolean, force_update_password: string}>}
 */
export function login(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/get_auth_token', params, resolve);
  });
}

/**
 * 当前登录用户修改自己的密码
 * 接口文档 — POST /fxzapi/user/update_current_pwd
 * @param {string} updatePwd - 新密码
 * @returns {Promise<{status: number, msg: string}>}
 */
export function updateCurrentPwd(updatePwd) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/update_current_pwd', { update_pwd: updatePwd }, resolve);
  });
}

/**
 * 忘记密码 — 发送邮箱验证链接
 * 接口文档 #8 — POST /fxzapi/user/pwd/forget/vertify_email
 * @param {object} params - { email_account: string }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function forgetPasswordVerifyEmail(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/pwd/forget/vertify_email', params, resolve);
  });
}

/**
 * 忘记密码 — 验证 token 并重置密码
 * 接口文档 #9 — POST /fxzapi/user/pwd/forget/modify_pwd
 * @param {object} params - { email_account: string, token: string, update_pwd: string }
 * @returns {Promise<{status: number, msg: string}>}
 */
export function forgetPasswordReset(params) {
  return new Promise((resolve) => {
    postHttp(FXZ_API_URI_PREFIX, '/user/pwd/forget/modify_pwd', params, resolve);
  });
}
